'use client';

import React, { useState } from 'react';
import {
  Users,
  CreditCard,
  AlertTriangle,
  TrendingUp,
  Calendar,
  PieChart,
  ArrowLeftRight,
  Download,
  FileText,
  TableProperties,
  RefreshCw,
} from 'lucide-react';
import ReportPreview from './ReportPreview';
import Icon from '@/components/ui/AppIcon';


export type ReportType =
  | 'investor-capital' |'active-loans' |'overdue-loans' |'interest-income' |'monthly-collections' |'profit-distribution' |'cash-flow';

const reportTypes: {
  id: ReportType;
  label: string;
  description: string;
  icon: React.ElementType;
  badge?: string;
}[] = [
  {
    id: 'investor-capital',
    label: 'Investor Capital',
    description: 'Contributions, profit share, and ROI per investor',
    icon: Users,
  },
  {
    id: 'active-loans',
    label: 'Active Loans',
    description: 'All active loans with balances and terms',
    icon: CreditCard,
  },
  {
    id: 'overdue-loans',
    label: 'Overdue Loans',
    description: 'Loans past due date with days overdue',
    icon: AlertTriangle,
    badge: '4',
  },
  {
    id: 'interest-income',
    label: 'Interest Income',
    description: 'Interest earned by period and loan',
    icon: TrendingUp,
  },
  {
    id: 'monthly-collections',
    label: 'Monthly Collections',
    description: 'Repayments received per month',
    icon: Calendar,
  },
  {
    id: 'profit-distribution',
    label: 'Profit Distribution',
    description: 'Profit allocated to each investor',
    icon: PieChart,
  },
  {
    id: 'cash-flow',
    label: 'Cash Flow',
    description: 'Capital inflows and outflows over time',
    icon: ArrowLeftRight,
  },
];

export default function ReportsHub() {
  const [selectedReport, setSelectedReport] = useState<ReportType>('active-loans');
  const [dateFrom, setDateFrom] = useState('2026-01-01');
  const [dateTo, setDateTo] = useState('2026-08-03');
  const [isExporting, setIsExporting] = useState<'pdf' | 'excel' | null>(null);

  // Backend integration point: POST /api/reports/export { type, dateFrom, dateTo, format }
  const handleExport = async (format: 'pdf' | 'excel') => {
    setIsExporting(format);
    await new Promise((r) => setTimeout(r, 1800));
    setIsExporting(null);
  };

  const currentReport = reportTypes.find((r) => r.id === selectedReport)!;

  return (
    <div className="flex flex-col lg:flex-row gap-4 min-h-[700px]">
      {/* Report type sidebar */}
      <div className="w-full lg:w-64 xl:w-72 shrink-0">
        <div className="card-elevated overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Report Type</p>
          </div>
          <ul className="p-2 space-y-0.5">
            {reportTypes.map((report) => {
              const Icon = report.icon;
              const active = selectedReport === report.id;
              return (
                <li key={`report-type-${report.id}`}>
                  <button
                    onClick={() => setSelectedReport(report.id)}
                    className={[
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-150 group',
                      active
                        ? 'bg-primary/10 border border-primary/20' :'hover:bg-muted border border-transparent',
                    ].join(' ')}
                  >
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${active ? 'bg-primary/20' : 'bg-muted group-hover:bg-muted/80'}`}>
                      <Icon size={14} className={active ? 'text-primary' : 'text-muted-foreground'} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs font-semibold truncate ${active ? 'text-primary' : 'text-foreground'}`}>
                        {report.label}
                      </p>
                      <p className="text-[10px] text-muted-foreground truncate mt-0.5">{report.description}</p>
                    </div>
                    {report.badge && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-negative-subtle text-negative border border-negative/20 shrink-0">
                        {report.badge}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Report preview panel */}
      <div className="flex-1 min-w-0">
        <div className="card-elevated h-full flex flex-col">
          {/* Report header */}
          <div className="px-5 py-4 border-b border-border">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-foreground">{currentReport.label} Report</h2>
                <p className="text-xs text-muted-foreground mt-0.5">{currentReport.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleExport('excel')}
                  disabled={isExporting !== null}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-muted text-foreground hover:bg-muted/80 border border-border transition-all duration-150 disabled:opacity-50"
                >
                  {isExporting === 'excel' ? (
                    <RefreshCw size={12} className="animate-spin" />
                  ) : (
                    <TableProperties size={12} />
                  )}
                  Export Excel
                </button>
                <button
                  onClick={() => handleExport('pdf')}
                  disabled={isExporting !== null}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold btn-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
                >
                  {isExporting === 'pdf' ? (
                    <RefreshCw size={12} className="animate-spin" />
                  ) : (
                    <FileText size={12} />
                  )}
                  Export PDF
                </button>
              </div>
            </div>

            {/* Date range filter */}
            <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-border">
              <div className="flex items-center gap-2">
                <label htmlFor="date-from" className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                  From
                </label>
                <input
                  id="date-from"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="input-base px-2.5 py-1.5 text-xs"
                />
              </div>
              <div className="flex items-center gap-2">
                <label htmlFor="date-to" className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                  To
                </label>
                <input
                  id="date-to"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="input-base px-2.5 py-1.5 text-xs"
                />
              </div>
              <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-primary bg-primary/10 border border-primary/20 hover:bg-primary/15 transition-all duration-150">
                <Download size={11} />
                Apply Filter
              </button>
            </div>
          </div>

          {/* Report content */}
          <div className="flex-1 overflow-auto">
            <ReportPreview reportType={selectedReport} dateFrom={dateFrom} dateTo={dateTo} />
          </div>
        </div>
      </div>
    </div>
  );
}